/** Monta Check-out pendente do mock a partir de GET /api/orders/completed-route. */

import { getBoardClienteExibicao } from '../../board/utils/clienteBoardDisplay';
import { fetchTechnicianPanelOsSummary } from '../services/technicianPanelApi';
import { openMapOsDetailModal } from './technicianPanelMockMapRender';
import { parseTimeToMinutes } from './technicianPanelTime';

function text(el, value) {
  el.textContent = value == null || value === '' ? '' : String(value);
}

const checkoutOsDetailCache = new Map();

async function openCheckoutOsDetail(order) {
  const os = String(order?.TB02122_NUMOS || order?.TB02115_CODIGO || '').trim();
  if (!os) return;

  const seedClient = getBoardClienteExibicao(order) || order?.TB01008_NOME || '';
  const seedEquip = order?.TB01010_RESUMIDO || order?.TB01010_NOME || '';
  const seedDefeito = order?.TB02115_NOME || '';
  const cached = checkoutOsDetailCache.get(os);

  openMapOsDetailModal({
    os,
    client: cached?.client || seedClient,
    equipamento: cached?.equipamento || seedEquip,
    serie: cached?.serie || '',
    pat: cached?.pat || '',
    bairro: cached?.bairro || '',
    cidade: cached?.cidade || '',
    endereco: cached?.endereco || '',
    defeito: cached?.defeito || seedDefeito,
    sla: cached?.sla || null,
    loadingExtras: !cached,
  });

  if (cached) return;

  try {
    const data = await fetchTechnicianPanelOsSummary(os);
    const spot = {
      os,
      client: data?.client || seedClient,
      equipamento: data?.equipamento || seedEquip,
      serie: data?.serie || '',
      pat: data?.pat || '',
      bairro: data?.bairro || '',
      cidade: data?.cidade || '',
      endereco: data?.endereco || '',
      defeito: data?.defeito || seedDefeito,
      sla: data?.sla || null,
    };
    if (data?.found) checkoutOsDetailCache.set(os, spot);
    openMapOsDetailModal(spot);
  } catch (_) {
    openMapOsDetailModal({
      os,
      client: seedClient,
      equipamento: seedEquip,
      defeito: seedDefeito,
    });
  }
}

function formatDateDDMMYYYY(dateValue) {
  if (!dateValue) return '—';
  if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(dateValue)) {
    return dateValue.slice(0, 10);
  }
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function pendingSince(order) {
  const fim = parseTimeToMinutes(order.TB02122_HORAFIM || order.TB02122_DATAFIM);
  if (fim == null) {
    const d = order.TB02122_DATAFIM ? new Date(order.TB02122_DATAFIM) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.max(0, Math.round((today - start) / 86400000));
    if (days <= 0) return 'hoje';
    if (days === 1) return '1d';
    return `${days}d`;
  }
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  let diff = nowMins - fim;
  if (diff < 0) diff = 0;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function sortCheckoutOrders(orders) {
  return [...(orders || [])].sort((a, b) => {
    const da = a.TB02122_DATAFIM ? new Date(a.TB02122_DATAFIM).getTime() : 0;
    const db = b.TB02122_DATAFIM ? new Date(b.TB02122_DATAFIM).getTime() : 0;
    if (db !== da) return db - da;
    const ha = String(a.TB02122_HORAFIM || '');
    const hb = String(b.TB02122_HORAFIM || '');
    return hb.localeCompare(ha);
  });
}

/**
 * @param {HTMLElement} root
 * @param {object[]} orders
 * @param {{ loading?: boolean }} [opts]
 */
export function applyTechnicianPanelCheckoutToMockDom(root, orders, opts = {}) {
  if (!root) return;
  const { loading = false } = opts;
  const list = sortCheckoutOrders(orders);

  const countLabel = root.querySelector('[data-tpm="co-count-label"]');
  if (countLabel) {
    if (loading && list.length === 0) {
      text(countLabel, 'Carregando…');
    } else {
      const n = list.length;
      text(
        countLabel,
        n === 1
          ? '1 atendimento executado aguarda check-out'
          : `${n} atendimentos executados aguardam check-out`
      );
    }
  }

  const tabCount = root.querySelector('[data-tab="co"] .tpm-count');
  if (tabCount) text(tabCount, String(list.length));

  const listEl = root.querySelector('[data-tpm="co-list"]');
  if (!listEl) return;
  listEl.replaceChildren();

  if (loading && list.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'tpm-co-empty';
    empty.setAttribute('data-tpm', 'co-empty');
    text(empty, 'Carregando check-outs…');
    listEl.appendChild(empty);
    return;
  }

  if (list.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'tpm-co-empty';
    empty.setAttribute('data-tpm', 'co-empty');
    text(empty, 'Nenhum check-out pendente');
    listEl.appendChild(empty);
    return;
  }

  const frag = document.createDocumentFragment();

  list.forEach((o) => {
    const os = String(o.TB02122_NUMOS || o.TB02115_CODIGO || '').trim();
    if (!os) return;
    const data = formatDateDDMMYYYY(o.TB02122_DATAFIM);
    const equip = o.TB01010_RESUMIDO || o.TB01010_NOME || '—';
    const cliente = getBoardClienteExibicao(o) || o.TB01008_NOME || '—';
    const cond = o.TB01055_NOME || 'Sem condição';
    const pend = pendingSince(o);

    const row = document.createElement('div');
    row.className = 'tpm-co-row';
    row.setAttribute('data-tpm', 'co-row');
    row.setAttribute('data-os', os);
    row.setAttribute('role', 'button');
    row.setAttribute('tabindex', '0');
    row.setAttribute('aria-label', `Abrir detalhe da OS ${os}`);
    const openDetail = (e) => {
      e.preventDefault();
      openCheckoutOsDetail(o);
    };
    row.addEventListener('click', openDetail);
    row.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      openDetail(e);
    });

    const time = document.createElement('div');
    time.className = 'tpm-co-time';
    const hh = document.createElement('span');
    hh.className = 'tpm-co-hh';
    text(hh, data);
    const hhLabel = document.createElement('span');
    hhLabel.className = 'tpm-co-hh-label';
    text(hhLabel, 'data');
    time.appendChild(hh);
    time.appendChild(hhLabel);
    row.appendChild(time);

    const rail = document.createElement('div');
    rail.className = 'tpm-co-rail';
    rail.setAttribute('aria-hidden', 'true');
    row.appendChild(rail);

    const body = document.createElement('div');
    body.className = 'tpm-co-body';
    const title = document.createElement('span');
    title.className = 'tpm-co-title';
    title.setAttribute('data-tpm-tip', `#${os} · ${equip}`);
    if (String(equip || '').length > 20) title.setAttribute('data-tpm-tip-wrap', '');
    text(title, `#${os} · ${equip}`);
    const sub = document.createElement('span');
    sub.className = 'tpm-co-sub';
    if (cliente) {
      sub.setAttribute('data-tpm-tip', cliente);
      if (String(cliente).length > 36) sub.setAttribute('data-tpm-tip-wrap', '');
    }
    text(sub, cliente);
    body.appendChild(title);
    body.appendChild(sub);
    row.appendChild(body);

    const pendEl = document.createElement('span');
    pendEl.className = 'tpm-co-pend';
    text(pendEl, `${pend} sem check-out`);
    row.appendChild(pendEl);

    const condEl = document.createElement('span');
    condEl.className = 'tpm-co-cond';
    if (cond) {
      condEl.setAttribute('data-tpm-tip', cond);
      if (String(cond).length > 36) condEl.setAttribute('data-tpm-tip-wrap', '');
    }
    text(condEl, cond);
    row.appendChild(condEl);

    frag.appendChild(row);
  });

  listEl.appendChild(frag);
}
